import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { fetchAllChats } from '../apis/chat';

const initialState = {
  chats: [],
  activeChat: null,
  isLoading: false,
  notifications: [],
  unreadCounts: {}, // { chatId: number }
};

export const fetchChats = createAsyncThunk('chats/fetchAll', async () => {
  try {
    const data = await fetchAllChats();
    return data;
  } catch (error) {
    toast.error('Could not load chats. Try again.');
    throw error;
  }
});

const chatsSlice = createSlice({
  name: 'chats',
  initialState,
  reducers: {
    setActiveChat: (state, { payload }) => {
      state.activeChat = payload;
      // Clear unread when opening chat
      if (payload?._id) {
        state.unreadCounts[payload._id] = 0;
        localStorage.setItem('activeChatId', payload._id);
      } else {
        localStorage.removeItem('activeChatId');
      }
    },
    setNotifications: (state, { payload }) => {
      state.notifications = payload;
    },
    addNotification: (state, { payload }) => {
      if (!state.notifications.find((n) => n._id === payload._id)) {
        state.notifications = [payload, ...state.notifications];
      }
      // Increment unread count
      const chatId = payload.chatId?._id || payload.chatId;
      if (chatId) {
        state.unreadCounts[chatId] = (state.unreadCounts[chatId] || 0) + 1;
      }
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setUnreadCount: (state, { payload: { chatId, count } }) => {
      state.unreadCounts[chatId] = count;
    },
    updateChatLatestMessage: (state, { payload }) => {
      const chatId = payload.chatId?._id || payload.chatId;
      const idx = state.chats.findIndex((c) => c._id === chatId);
      if (idx !== -1) {
        state.chats[idx].latestMessage = payload;
        // Move to top
        const [chat] = state.chats.splice(idx, 1);
        state.chats.unshift(chat);
      }
    },
    clearChatMessagesInState: (state, { payload: { chatId } }) => {
      const idx = state.chats.findIndex((c) => c._id === chatId);
      if (idx !== -1) {
        state.chats[idx].latestMessage = null;
      }
      if (state.activeChat && state.activeChat._id === chatId) {
        state.activeChat = { ...state.activeChat, latestMessage: null };
      }
    },
    deleteChatInState: (state, { payload: { chatId } }) => {
      state.chats = state.chats.filter((c) => c._id !== chatId);
      if (state.activeChat && state.activeChat._id === chatId) {
        state.activeChat = null;
        localStorage.removeItem('activeChatId');
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchChats.fulfilled, (state, { payload }) => {
        state.chats = payload ?? [];
        state.isLoading = false;
        const storedActiveId = localStorage.getItem('activeChatId');
        if (storedActiveId && (!state.activeChat || state.activeChat._id !== storedActiveId)) {
          const found = state.chats.find((c) => c._id === storedActiveId);
          if (found) {
            state.activeChat = found;
          }
        }
      })
      .addCase(fetchChats.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  setActiveChat,
  setNotifications,
  addNotification,
  clearNotifications,
  setUnreadCount,
  updateChatLatestMessage,
  clearChatMessagesInState,
  deleteChatInState,
} = chatsSlice.actions;

export default chatsSlice.reducer;
